/**
 * @file <bk-cart-items>: the interactive part of the cart drawer and of the cart page.
 *
 * Responsibilities
 * - Quantity changes and removals through the Cart API, addressed by line item key (so a removal
 *   never shifts the next request onto the wrong line). Stepper clicks are merged (debounced) and
 *   every cart mutation runs one request at a time, so responses can never arrive out of order.
 *   Quantity 0 removes the line.
 * - Errors: a 422 (for example more than the stock) lands in that line's role="alert" slot with
 *   the previous quantity restored; a quantity capped by Shopify is reported the same way.
 * - Order note (debounced, POST cart/update.js without re-rendering) and discount codes
 *   (POST cart/update.js, re-rendered, with an "invalid" or "applied at checkout" message).
 * - Re-rendering its own [data-cart-content] from the Section Rendering API (the section id is in
 *   data-sections) after every cart change, keeping focus, caret, scroll position, open <details>
 *   and a note being typed. Changes made by other components arrive through `bk:cart:update`.
 * - Every [data-cart-count] bubble of the page follows the cart (empty and hidden at 0).
 * - The visitor's saved delivery area (localStorage "bk:wilaya", see @theme/utils) is named in the
 *   delivery line and appended to the WhatsApp cart message.
 *
 * Markup: sections/cart-drawer.liquid and sections/main-cart.liquid
 *   <bk-cart-items data-sections="<section id>" data-error-message="…" data-capped-message="…[quantity]…"
 *     data-discount-invalid="…[code]…" data-discount-checkout="…[code]…" data-note-saved="…"
 *     data-updated-message="…[total]…" data-removed-message="…[title]…" data-delivery-to="…[place]…">
 *     <p role="status" data-cart-status></p>
 *     <div data-cart-content>… snippets/cart-line-item, snippets/cart-summary …</div>
 *   </bk-cart-items>
 * Every visible string comes from those data-* attributes, rendered with `| t`.
 *
 * Without JavaScript the cart form posts `updates[]`, the note and `checkout` to the cart URL and
 * removal is a plain link, so none of this is required to order.
 */
import { changeCart, debounce, fetchSections, getWilaya, publish, subscribe } from '@theme/utils';

/** Stepper clicks within this window are merged into one request. */
const STEP_DEBOUNCE_MS = 300;

/** The note is saved this long after the last keystroke. */
const NOTE_DEBOUNCE_MS = 600;

/** Shared with assets/cart-drawer.js: one count subscription per page, whichever module loads first. */
const COUNT_SYNC = Symbol.for('bk.cartCountSync');

/**
 * Locale-aware storefront root, always ending with "/" ("/" or "/ar/").
 * @returns {string}
 */
function storefrontRoot() {
  const root = window.Shopify?.routes?.root ?? '/';
  return root.endsWith('/') ? root : `${root}/`;
}

/**
 * Write `count` into every cart count bubble of the page (header, mobile tab bar): the text is
 * empty and the element hidden at 0.
 * @param {number} count
 */
export function setCartCount(count) {
  for (const bubble of document.querySelectorAll('[data-cart-count]')) {
    if (!(bubble instanceof HTMLElement)) continue;
    bubble.textContent = count > 0 ? String(count) : '';
    bubble.hidden = count === 0;
  }
}

const countHost = /** @type {Record<symbol, unknown>} */ (/** @type {unknown} */ (document));
if (!countHost[COUNT_SYNC]) {
  countHost[COUNT_SYNC] = subscribe('bk:cart:update', (detail) => {
    const count = detail?.cart?.item_count;
    if (typeof count === 'number') setCartCount(count);
  });
}

/**
 * @typedef {{ quantity: number }} PendingChange
 * @typedef {{ cart?: any, sections?: Record<string, string | null>, source?: string }} CartUpdateDetail
 */

export class BkCartItems extends HTMLElement {
  /** @type {AbortController | null} Listeners and requests of this element (aborted on disconnect). */
  #controller = null;

  /** @type {AbortController | null} */
  #noteRequest = null;

  /** @type {AbortController | null} */
  #refreshRequest = null;

  /** @type {Array<() => void>} */
  #unsubscribers = [];

  /** @type {Map<string, PendingChange>} Line key to the quantity still to send (the latest wins). */
  #pending = new Map();

  /** @type {Promise<unknown>} Tail of the chain that runs cart mutations one at a time. */
  #lock = Promise.resolve();

  #draining = false;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #stepTimer;

  /** @type {CartUpdateDetail | null} A response not rendered yet because newer changes were queued. */
  #deferred = null;

  /** @type {string | null} Focus target used when the focused control disappears in a re-render. */
  #focusFallback = null;

  /** @type {((textarea: HTMLTextAreaElement) => void) & { cancel: () => void }} */
  #saveNoteLater = debounce((/** @type {HTMLTextAreaElement} */ textarea) => this.#saveNote(textarea), NOTE_DEBOUNCE_MS);

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('click', (event) => this.#handleClick(event), { signal });
    this.addEventListener('change', (event) => this.#handleChange(event), { signal });
    this.addEventListener('keydown', (event) => this.#handleKeydown(event), { signal });
    this.addEventListener('input', (event) => this.#handleInput(event), { signal });
    window.addEventListener(
      'pageshow',
      (event) => {
        if (event.persisted) this.#refresh();
      },
      { signal },
    );

    this.#unsubscribers = [
      subscribe('bk:cart:update', (detail) => this.#handleCartUpdate(detail)),
      subscribe('bk:wilaya:change', () => this.#applyWilaya()),
    ];
    this.#applyWilaya();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#noteRequest?.abort();
    this.#noteRequest = null;
    this.#refreshRequest?.abort();
    this.#refreshRequest = null;
    clearTimeout(this.#stepTimer);
    this.#saveNoteLater.cancel();
    for (const unsubscribe of this.#unsubscribers) unsubscribe();
    this.#unsubscribers = [];
    this.#pending.clear();
  }

  /** @returns {string[]} Section ids this component re-renders with (the first one is its own). */
  get #sectionIds() {
    return (this.dataset.sections ?? '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
  }

  /** @returns {AbortSignal | undefined} */
  get #signal() {
    return this.#controller?.signal;
  }

  /**
   * Run a cart mutation after the previous one settled.
   * @template T
   * @param {() => Promise<T>} task
   * @returns {Promise<T>}
   */
  #exclusive(task) {
    const run = this.#lock.then(task);
    this.#lock = run.then(
      () => undefined,
      () => undefined,
    );
    return run;
  }

  /**
   * A translated message from a data-* attribute, with its [placeholders] filled in.
   * @param {string} name - dataset key, e.g. 'cappedMessage'
   * @param {Record<string, string | number>} [values]
   * @returns {string}
   */
  #message(name, values = {}) {
    const template = this.dataset[name] ?? '';
    return template.replace(/\[(\w+)\]/g, (match, key) => (key in values ? String(values[key]) : match));
  }

  /** @param {string} message */
  #announce(message) {
    const region = this.querySelector('[data-cart-status]');
    if (!(region instanceof HTMLElement) || !message) return;
    region.textContent = '';
    requestAnimationFrame(() => {
      region.textContent = message;
    });
  }

  /**
   * @param {string} key - Line item key
   * @returns {HTMLElement | null}
   */
  #row(key) {
    for (const row of this.querySelectorAll('[data-cart-line]')) {
      if (row instanceof HTMLElement && row.dataset.key === key) return row;
    }
    return null;
  }

  /**
   * Put a message in a line's alert slot, or in the cart-level one when the line is gone. Filled on
   * the next frame, so a live region that was just re-rendered still announces it.
   * @param {string | null} key
   * @param {string} message
   */
  #showError(key, message) {
    requestAnimationFrame(() => {
      const row = key ? this.#row(key) : null;
      const slot = row?.querySelector('[data-line-error]') ?? this.querySelector('[data-cart-error]');
      if (slot instanceof HTMLElement) slot.textContent = message;
    });
  }

  /** @param {HTMLElement | null} row */
  #clearErrors(row) {
    const cartError = this.querySelector('[data-cart-error]');
    if (cartError instanceof HTMLElement) cartError.textContent = '';
    const slot = row?.querySelector('[data-line-error]');
    if (slot instanceof HTMLElement) slot.textContent = '';
  }

  /** @param {boolean} busy */
  #setTotalBusy(busy) {
    const total = this.querySelector('[data-cart-total]');
    if (total instanceof HTMLElement) total.toggleAttribute('aria-busy', busy);
  }

  /** @param {MouseEvent} event */
  #handleClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const remove = target.closest('[data-cart-remove]');
    if (remove instanceof HTMLElement && this.contains(remove)) {
      event.preventDefault();
      const row = remove.closest('[data-cart-line]');
      if (row instanceof HTMLElement && row.dataset.key) this.#queueChange(row.dataset.key, 0, { immediate: true });
      return;
    }

    const apply = target.closest('[data-discount-apply]');
    if (apply instanceof HTMLElement && this.contains(apply)) {
      event.preventDefault();
      this.#applyDiscount();
      return;
    }

    const removeCode = target.closest('[data-discount-remove]');
    if (removeCode instanceof HTMLElement && this.contains(removeCode)) {
      event.preventDefault();
      const pill = removeCode.closest('[data-discount-code]');
      if (pill instanceof HTMLElement) this.#removeDiscount(pill.dataset.discountCode ?? '');
    }
  }

  /** @param {Event} event */
  #handleChange(event) {
    const input = event.target instanceof Element ? event.target.closest('[data-cart-quantity]') : null;
    if (!(input instanceof HTMLInputElement) || !this.contains(input)) return;

    const row = input.closest('[data-cart-line]');
    const key = row instanceof HTMLElement ? (row.dataset.key ?? '') : '';
    if (!key) return;

    const quantity = Number(input.value);
    if (input.value.trim() === '' || !Number.isInteger(quantity) || quantity < 0) {
      input.value = input.defaultValue;
      return;
    }

    const committed = Number(/** @type {HTMLElement} */ (row).dataset.quantity);
    if (quantity === committed && !this.#pending.has(key)) return;

    // Stepper buttons dispatch an untrusted change: quick clicks are merged. Typed values commit at once.
    this.#queueChange(key, quantity, { immediate: event.isTrusted });
  }

  /** @param {KeyboardEvent} event */
  #handleKeydown(event) {
    if (event.key !== 'Enter' || event.isComposing) return;
    const target = event.target;
    if (target instanceof HTMLInputElement && target.matches('[data-discount-input]')) {
      // Enter applies the code instead of submitting the cart form.
      event.preventDefault();
      this.#applyDiscount();
    }
  }

  /** @param {Event} event */
  #handleInput(event) {
    const note = event.target instanceof Element ? event.target.closest('[data-note-input]') : null;
    if (!(note instanceof HTMLTextAreaElement) || !this.contains(note)) return;
    const status = this.querySelector('[data-note-status]');
    if (status instanceof HTMLElement) status.textContent = '';
    this.#saveNoteLater(note);
  }

  /**
   * Queue a quantity for a line.
   * @param {string} key
   * @param {number} quantity
   * @param {{ immediate?: boolean }} [options]
   */
  #queueChange(key, quantity, { immediate = false } = {}) {
    this.#pending.set(key, { quantity });
    this.#clearErrors(this.#row(key));
    this.#setTotalBusy(true);
    clearTimeout(this.#stepTimer);
    if (immediate) this.#drain();
    else this.#stepTimer = setTimeout(() => this.#drain(), STEP_DEBOUNCE_MS);
  }

  /** Send every queued change, one request at a time. */
  #drain() {
    if (this.#draining) return;
    this.#draining = true;
    this.#exclusive(async () => {
      while (this.#pending.size > 0 && this.isConnected) {
        const [key, change] = /** @type {[string, PendingChange]} */ (this.#pending.entries().next().value);
        this.#pending.delete(key);
        await this.#sendChange(key, change);
      }
    }).finally(() => {
      this.#draining = false;
      if (this.#pending.size > 0 && this.isConnected) this.#drain();
      else this.#setTotalBusy(false);
    });
  }

  /**
   * @param {string} key
   * @param {PendingChange} change
   */
  async #sendChange(key, change) {
    const row = this.#row(key);
    row?.setAttribute('aria-busy', 'true');
    const title = row?.querySelector('[data-line-title]')?.textContent?.trim() ?? '';
    if (change.quantity === 0 && row) this.#focusFallback = this.#neighbourFocusKey(row);

    try {
      // changeCart publishes bk:cart:update; #handleCartUpdate re-renders unless newer changes wait.
      const cart = await changeCart(key, change.quantity, { sections: this.#sectionIds, signal: this.#signal });
      this.#afterChange(key, change, cart, title);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      this.#handleChangeError(key, error);
    }
  }

  /**
   * Report what the server did with the requested quantity.
   * @param {string} key
   * @param {PendingChange} change
   * @param {any} cart
   * @param {string} title
   */
  #afterChange(key, change, cart, title) {
    if (this.#pending.size > 0) return; // A newer change is on its way; it reports instead.

    if (change.quantity === 0) {
      this.#announce(this.#message('removedMessage', { title }));
      return;
    }

    const item = (cart?.items ?? []).find((/** @type {{ key: string }} */ line) => line.key === key);
    if (!item) {
      this.#showError(null, this.dataset.errorMessage ?? '');
      return;
    }
    if (item.quantity < change.quantity) {
      this.#showError(key, this.#message('cappedMessage', { quantity: item.quantity }));
      return;
    }
    this.#announce(this.#message('updatedMessage', { total: this.#totalText() }));
  }

  /**
   * @param {string} key
   * @param {unknown} error
   */
  #handleChangeError(key, error) {
    // A response skipped while this request waited is still the truth: show it first.
    if (this.#deferred) this.#renderDetail(this.#deferred);
    this.#focusFallback = null;

    const row = this.#row(key);
    row?.removeAttribute('aria-busy');
    const input = row?.querySelector('[data-cart-quantity]');
    if (input instanceof HTMLInputElement && input.value !== input.defaultValue) {
      input.value = input.defaultValue;
      // Lets the stepper re-sync its buttons; the cart ignores a change back to the saved quantity.
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const description = /** @type {{ description?: unknown }} */ (error)?.description;
    const readable = typeof description === 'string' && description && !/^HTTP \d+$/.test(description) ? description : '';
    this.#showError(key, readable || this.dataset.errorMessage || '');
  }

  /**
   * Where focus goes when a removed line held it: the next line's title, else the previous one's,
   * else the empty state's call to action.
   * @param {HTMLElement} row
   * @returns {string}
   */
  #neighbourFocusKey(row) {
    const rows = [...this.querySelectorAll('[data-cart-line]')];
    const index = rows.indexOf(row);
    const neighbour = rows[index + 1] ?? rows[index - 1];
    return neighbour instanceof HTMLElement ? `title-${neighbour.dataset.key}` : 'empty-action';
  }

  /** @returns {string} For example "Subtotal 438 000 DA", read from the rendered summary. */
  #totalText() {
    const row = this.querySelector('[data-cart-total]')?.closest('div');
    return row?.textContent?.replace(/\s+/g, ' ').trim() ?? '';
  }

  /** @returns {string[]} Discount codes shown as applied, as rendered. */
  #discountCodes() {
    const codes = [];
    for (const pill of this.querySelectorAll('[data-discount-code]')) {
      const code = pill instanceof HTMLElement ? pill.dataset.discountCode : '';
      if (code) codes.push(code);
    }
    return codes;
  }

  /**
   * @param {string} message
   * @param {'error' | 'info'} [tone]
   */
  #setDiscountMessage(message, tone = 'error') {
    requestAnimationFrame(() => {
      const slot = this.querySelector('[data-discount-message]');
      if (!(slot instanceof HTMLElement)) return;
      slot.dataset.tone = tone;
      slot.textContent = message;
    });
  }

  /** Apply the typed code on top of the codes already applied. */
  async #applyDiscount() {
    const input = this.querySelector('[data-discount-input]');
    if (!(input instanceof HTMLInputElement)) return;

    const code = input.value.trim();
    if (!code) {
      input.focus();
      return;
    }

    const same = (/** @type {string} */ value) => value.toLowerCase() === code.toLowerCase();
    const codes = this.#discountCodes();
    if (codes.some(same)) {
      input.value = '';
      return;
    }

    this.#setDiscountMessage('');
    const cart = await this.#updateCart({ discount: [...codes, code].join(',') });
    if (cart === undefined) return;
    if (!cart) {
      this.#setDiscountMessage(this.dataset.errorMessage ?? '');
      return;
    }

    const entry = (cart.discount_codes ?? []).find((/** @type {{ code: string }} */ item) => same(item.code));
    if (entry && entry.applicable === false) {
      this.#setDiscountMessage(this.#message('discountInvalid', { code }));
    } else if (entry && !this.#discountCodes().some(same)) {
      // Valid, but nothing in the cart shows it yet (for example a shipping discount).
      this.#setDiscountMessage(this.#message('discountCheckout', { code }), 'info');
    } else {
      this.#announce(this.#message('updatedMessage', { total: this.#totalText() }));
    }
  }

  /** @param {string} code */
  async #removeDiscount(code) {
    if (!code) return;
    const codes = this.#discountCodes().filter((value) => value.toLowerCase() !== code.toLowerCase());
    this.#focusFallback = 'discount-input';
    const cart = await this.#updateCart({ discount: codes.join(',') });
    if (cart === undefined) return;
    if (cart) this.#announce(this.#message('updatedMessage', { total: this.#totalText() }));
    else this.#setDiscountMessage(this.dataset.errorMessage ?? '');
  }

  /**
   * The note never changes the totals: save it without re-rendering, so typing is never interrupted.
   * @param {HTMLTextAreaElement} textarea
   */
  async #saveNote(textarea) {
    this.#noteRequest?.abort();
    this.#noteRequest = new AbortController();
    const cart = await this.#postCart({ note: textarea.value }, { sections: false, signal: this.#noteRequest.signal });
    if (cart === undefined) return; // replaced by a newer keystroke
    const status = this.querySelector('[data-note-status]');
    if (!(status instanceof HTMLElement)) return;
    status.textContent = '';
    requestAnimationFrame(() => {
      status.textContent = cart ? (this.dataset.noteSaved ?? '') : (this.dataset.errorMessage ?? '');
    });
  }

  /**
   * POST cart/update.js with this component's sections, after any running change, then publish the
   * update (which re-renders every cart view on the page).
   * @param {Record<string, unknown>} body
   * @returns {Promise<any | null | undefined>} The cart, null when the request failed, undefined when aborted
   */
  #updateCart(body) {
    this.#setTotalBusy(true);
    return this.#exclusive(async () => {
      const cart = await this.#postCart(body, { sections: true, signal: this.#signal });
      if (cart) publish('bk:cart:update', { cart, sections: cart.sections ?? {}, source: 'update' });
      else this.#setTotalBusy(false);
      return cart;
    });
  }

  /**
   * @param {Record<string, unknown>} body
   * @param {{ sections: boolean, signal?: AbortSignal }} options
   * @returns {Promise<any | null | undefined>} The cart, null when the request failed, undefined when aborted
   */
  async #postCart(body, { sections, signal }) {
    /** @type {Record<string, unknown>} */
    const payload = { ...body };
    if (sections) {
      payload.sections = this.#sectionIds.join(',');
      payload.sections_url = window.location.pathname;
    }

    try {
      const response = await fetch(new URL(`${storefrontRoot()}cart/update.js`, window.location.origin), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(payload),
        signal,
      });
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return undefined;
      return null;
    }
  }

  /** @param {CartUpdateDetail} detail */
  #handleCartUpdate(detail) {
    if (this.#pending.size > 0) {
      // A newer change is queued: its response is rendered instead of this one.
      this.#deferred = detail;
      return;
    }
    this.#renderDetail(detail);
  }

  /** @param {CartUpdateDetail} detail */
  #renderDetail(detail) {
    this.#deferred = null;
    const html = detail?.sections?.[this.#sectionIds[0]];
    if (typeof html === 'string' && html && this.#render(html)) return;
    this.#refresh();
  }

  /** Re-fetch this component's own section (a change made elsewhere, or a page restored from cache). */
  async #refresh() {
    const [id] = this.#sectionIds;
    if (!id) return;
    this.#refreshRequest?.abort();
    this.#refreshRequest = new AbortController();
    try {
      const sections = await fetchSections(window.location.href, [id], { signal: this.#refreshRequest.signal });
      const html = sections[id];
      if (typeof html === 'string' && html) this.#render(html);
    } catch {
      // Keep the current markup; the next change re-renders it.
    }
  }

  /**
   * Replace the server-rendered content, keeping focus (and caret), scroll offset, the state of
   * the <details> and a note being typed.
   * @param {string} html - Section HTML from the Section Rendering API
   * @returns {boolean} False when the markup could not be used
   */
  #render(html) {
    const current = this.querySelector('[data-cart-content]');
    const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-content]');
    if (!(current instanceof HTMLElement) || !(next instanceof HTMLElement)) return false;

    const active = document.activeElement;
    const hadFocus = active instanceof HTMLElement && current.contains(active);
    const focusKey = hadFocus ? (active.closest('[data-focus-key]')?.getAttribute('data-focus-key') ?? null) : null;
    const caret =
      hadFocus && (active instanceof HTMLTextAreaElement || (active instanceof HTMLInputElement && active.type === 'text'))
        ? [active.selectionStart, active.selectionEnd]
        : null;

    /** @type {Map<string, boolean>} */
    const detailsState = new Map();
    for (const details of current.querySelectorAll('details[data-details-key]')) {
      if (details instanceof HTMLDetailsElement) detailsState.set(details.dataset.detailsKey ?? '', details.open);
    }
    const note = current.querySelector('[data-note-input]');
    const noteValue = note instanceof HTMLTextAreaElement ? note.value : null;
    const discount = current.querySelector('[data-discount-input]');
    const discountValue = discount instanceof HTMLInputElement && active === discount ? discount.value : '';
    const scroller = current.querySelector('[data-cart-scroll]');
    const scrollTop = scroller instanceof HTMLElement ? scroller.scrollTop : 0;

    current.className = next.className;
    current.innerHTML = next.innerHTML;

    for (const details of current.querySelectorAll('details[data-details-key]')) {
      if (!(details instanceof HTMLDetailsElement)) continue;
      const key = details.dataset.detailsKey ?? '';
      if (detailsState.has(key)) details.open = Boolean(detailsState.get(key));
    }
    const nextNote = current.querySelector('[data-note-input]');
    if (nextNote instanceof HTMLTextAreaElement && noteValue !== null) nextNote.value = noteValue;
    const nextDiscount = current.querySelector('[data-discount-input]');
    if (nextDiscount instanceof HTMLInputElement && discountValue) nextDiscount.value = discountValue;
    const nextScroller = current.querySelector('[data-cart-scroll]');
    if (nextScroller instanceof HTMLElement && scrollTop) nextScroller.scrollTop = scrollTop;

    this.#restoreFocus(current, hadFocus, focusKey, caret);
    this.#applyWilaya();
    // Accelerated checkout buttons rendered by Shopify initialise themselves again.
    window.Shopify?.PaymentButton?.init?.();
    return true;
  }

  /**
   * @param {HTMLElement} content
   * @param {boolean} hadFocus
   * @param {string | null} focusKey
   * @param {Array<number | null> | null} caret
   */
  #restoreFocus(content, hadFocus, focusKey, caret) {
    const fallbackKey = this.#focusFallback;
    this.#focusFallback = null;
    if (!hadFocus) return;

    const byKey = (/** @type {string | null} */ key) =>
      key ? content.querySelector(`[data-focus-key="${CSS.escape(key)}"]`) : null;
    const target =
      byKey(focusKey) ??
      byKey(fallbackKey) ??
      content.querySelector('a[href], button:not([disabled]), input:not([disabled]), textarea');
    if (!(target instanceof HTMLElement)) return;

    target.focus({ preventScroll: true });
    if (caret && (target instanceof HTMLTextAreaElement || target instanceof HTMLInputElement)) {
      try {
        target.setSelectionRange(caret[0], caret[1]);
      } catch {
        // Number inputs do not support selection ranges.
      }
    }
  }

  /** Name the visitor's saved delivery area in the delivery line and in the WhatsApp message. */
  #applyWilaya() {
    const wilaya = getWilaya();
    const line = wilaya ? this.#message('deliveryTo', { place: wilaya }) : '';

    const place = this.querySelector('[data-delivery-place]');
    if (place instanceof HTMLElement) {
      place.textContent = line;
      place.hidden = !line;
    }

    const link = this.querySelector('[data-cart-whatsapp]');
    if (!(link instanceof HTMLAnchorElement)) return;
    const base = link.dataset.baseHref ?? link.href;
    link.dataset.baseHref = base;
    if (!line) {
      link.href = base;
      return;
    }
    try {
      const url = new URL(base);
      const text = url.searchParams.get('text') ?? '';
      url.search = `?text=${encodeURIComponent(`${text}\n${line}`)}`;
      link.href = url.toString();
    } catch {
      link.href = base;
    }
  }
}

if (!customElements.get('bk-cart-items')) customElements.define('bk-cart-items', BkCartItems);
